<template>
  <div class="input-wrapper" :class="{ 'is-invalid': isInvalid && required }">
    <label v-if="label" :for="id">{{ label }}</label>
    <slot v-else name="label"></slot>
    <div class="input-container">
      <div class="dropdown">
        <button
          class="btn dropdown-toggle"
          :class="{
            'text-muted': !selectedLabel,
            'is-invalid': isInvalid && required,
          }"
          type="button"
          :id="id"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          :aria-label="label || placeholder || 'Select an option'"
          ref="dropdownButton"
          @click="handleDropdownOpen"
          @keydown="handleTriggerKeydown"
        >
          <span class="select-trigger-text">{{ selectedLabel || placeholder || "Select" }}</span>
        </button>
        <ul class="dropdown-menu" :aria-labelledby="id">
          <li v-if="searchable" class="search-container">
            <input
              type="search"
              :name="`${id}-option-search`"
              autocomplete="off"
              autocapitalize="off"
              :spellcheck="false"
              class="search-input"
              v-model="searchQuery"
              placeholder="Search..."
              aria-label="Search options"
              @click.stop
              @keydown.down.prevent="moveHighlight(1)"
              @keydown.up.prevent="moveHighlight(-1)"
              @keydown.enter.prevent="selectHighlightedOption"
              ref="searchInput"
            />
          </li>
          <li v-if="!dropdownItems.length" class="px-3 py-2 text-muted" role="status">
            No matching options
          </li>
          <li
            v-for="(item, index) in dropdownItems"
            :key="`${item.type}-${item.value}`"
          >
            <a
              class="dropdown-item"
              href="#"
              @click.prevent="handleItemSelection(item)"
              :class="{
                active: item.value === localModelValue,
                'keyboard-highlighted': index === highlightedIndex,
              }"
            >
              <component :is="item.icon" v-if="item.icon" class="dropdown-item-icon" />
              {{ item.label }}
            </a>
          </li>
        </ul>
      </div>
      <select
        :id="`${id}-hidden`"
        :name="id"
        :autocomplete="autocomplete"
        :required="required"
        v-model="localModelValue"
        class="validation-select"
        :aria-label="label || placeholder || 'Select an option'"
        @invalid="handleInvalid"
      >
        <option value="">{{ placeholder || "Select" }}</option>
        <option
          v-for="option in options"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
        <option
          v-if="allowCustom && localModelValue && !options.some(option => option.value == localModelValue)"
          :value="localModelValue"
        >{{ localModelValue }}</option>
        <option v-if="addOption" :value="addOption.value">
          {{ addOption.label }}
        </option>
      </select>
      <div v-if="isInvalid && required" class="invalid-feedback">
        Please select an option
      </div>
      <slot name="append"></slot>
    </div>
  </div>
</template>
<script>
import CaretDownIcon from "../icons/CaretDownIcon.vue";
import { Dropdown } from "bootstrap";
import debounce from "lodash/debounce";

export default {
  name: "BaseSelectInput",
  components: {
    CaretDownIcon,
  },
  emits: ["update:modelValue", "addOption", "input", "search"],
  props: {
    modelValue: {
      type: [Number, String],
      default: null,
    },
    placeholder: String,
    label: String,
    id: {
      type: String,
      default: () => {
        return (
          "base-select-input-" + Math.random().toString(36).substring(2, 15)
        );
      },
    },
    type: {
      type: String,
      default: "text",
    },
    required: {
      type: Boolean,
      default: false,
    },
    autocomplete: {
      type: String,
      default: "off",
    },
    options: {
      type: Array,
      default: () => [],
    },
    addOption: {
      type: [Object, Array],
      default: null,
    },
    allowCustom: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localModelValue: this.modelValue,
      dropdownInstance: null,
      isInvalid: false,
      searchQuery: "",
      debouncedSearch: null,
      highlightedIndex: -1,
    };
  },
  computed: {
    selectedLabel() {
      const selected = this.options.find(
        (option) => option.value == this.localModelValue
      );
      return selected ? selected.label : (this.allowCustom ? this.localModelValue : null);
    },
    normalizedAddOptions() {
      if (!this.addOption) return [];
      return Array.isArray(this.addOption) ? this.addOption : [this.addOption];
    },
    filteredOptions() {
      if (!this.searchQuery) return this.options;
      const query = this.searchQuery.toLowerCase();
      return this.options.filter((option) =>
        option.label.toLowerCase().includes(query) ||
        String(option.value ?? "").toLowerCase().includes(query)
      );
    },
    dropdownItems() {
      const optionItems = this.filteredOptions.map((option) => ({
        ...option,
        type: "option",
      }));

      const query = this.searchQuery.toLowerCase();
      this.normalizedAddOptions.forEach((opt) => {
        if (!query || opt.label.toLowerCase().includes(query)) {
          optionItems.push({ ...opt, type: "add" });
        }
      });

      const customValue = this.searchQuery.trim();
      const exactMatch = this.options.some(option =>
        option.label.toLowerCase() === customValue.toLowerCase() ||
        String(option.value).toLowerCase() === customValue.toLowerCase()
      );
      if (this.allowCustom && customValue && !exactMatch) {
        optionItems.push({ type: "custom", value: customValue, label: `Use “${customValue}”` });
      }
      return optionItems;
    },
  },
  methods: {
    handleInvalid(event) {
      if (!this.required) return; // Only handle validation if required
      event.preventDefault();
      this.isInvalid = true;
      // Focus the dropdown button
      this.$nextTick(() => {
        if (this.$refs.dropdownButton) {
          this.$refs.dropdownButton.focus();
        }
      });
    },
    selectOption(value) {
      this.localModelValue = value;
      this.isInvalid = false;
      this.searchQuery = ""; // Clear search when option is selected
      this.highlightedIndex = -1;
      this.$emit("update:modelValue", value);
      if (this.dropdownInstance) {
        this.dropdownInstance.hide();
      }
      this.$emit("input", value);
      this.$nextTick(() => {
        const hiddenSelect = this.$el.querySelector(`#${this.id}-hidden`);
        if (hiddenSelect) {
          hiddenSelect.dispatchEvent(new Event("change"));
        }
      });
    },
    initDropdown() {
      const dropdownEl = this.$el.querySelector(".dropdown-toggle");
      if (dropdownEl) {
        this.dropdownInstance = new Dropdown(dropdownEl);
      }
    },
    emitAddOption(value) {
      this.$emit("addOption", value);
    },
    handleItemSelection(item) {
      if (item.type === "add") {
        this.highlightedIndex = -1;
        if (this.dropdownInstance) this.dropdownInstance.hide();
        this.emitAddOption(item.value);
        return;
      }

      this.selectOption(item.value);
    },
    setInitialHighlight() {
      if (!this.dropdownItems.length) {
        this.highlightedIndex = -1;
        return;
      }

      const selectedIndex = this.dropdownItems.findIndex(
        (item) => item.value == this.localModelValue
      );
      this.highlightedIndex = selectedIndex >= 0 ? selectedIndex : 0;
      this.$nextTick(() => {
        this.scrollHighlightedIntoView();
      });
    },
    moveHighlight(step) {
      if (!this.dropdownItems.length) {
        this.highlightedIndex = -1;
        return;
      }

      if (this.highlightedIndex === -1) {
        this.highlightedIndex = step > 0 ? 0 : this.dropdownItems.length - 1;
      } else {
        this.highlightedIndex =
          (this.highlightedIndex + step + this.dropdownItems.length) %
          this.dropdownItems.length;
      }

      this.$nextTick(() => {
        this.scrollHighlightedIntoView();
      });
    },
    selectHighlightedOption() {
      if (this.highlightedIndex < 0) return;
      const highlightedItem = this.dropdownItems[this.highlightedIndex];
      if (!highlightedItem) return;
      this.handleItemSelection(highlightedItem);
      this.$nextTick(() => {
        this.focusDropdownButton();
      });
    },
    scrollHighlightedIntoView() {
      const dropdownItems = this.$el.querySelectorAll(".dropdown-item");
      const highlightedEl = dropdownItems[this.highlightedIndex];
      if (highlightedEl) {
        highlightedEl.scrollIntoView({ block: "nearest" });
      }
    },
    focusDropdownButton() {
      if (this.$refs.dropdownButton) {
        this.$refs.dropdownButton.focus();
      }
    },
    isPrintableKey(event) {
      return (
        event.key &&
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      );
    },
    handleTriggerKeydown(event) {
      if (!this.searchable || !this.isPrintableKey(event)) return;

      event.preventDefault();
      if (this.dropdownInstance) {
        this.dropdownInstance.show();
      }

      this.$nextTick(() => {
        this.setInitialHighlight();
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
          this.searchQuery = `${this.searchQuery}${event.key}`;
        }
      });
    },
    handleDropdownOpen() {
      this.$nextTick(() => {
        this.setInitialHighlight();
        if (this.searchable && this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }
      });
    },
  },
  watch: {
    modelValue(newVal) {
      this.localModelValue = newVal;
    },
    searchQuery() {
      this.setInitialHighlight();
      this.debouncedSearch();
    },
  },
  mounted() {
    this.initDropdown();
    this.debouncedSearch = debounce(() => {
      this.$emit("search", this.searchQuery);
    }, 500);
  },
  updated() {
    // Re-initialize dropdown if needed
    if (!this.dropdownInstance) {
      this.initDropdown();
    }
  },
  beforeUnmount() {
    // Clean up dropdown instance
    if (this.dropdownInstance) {
      this.dropdownInstance = null;
    }
  },
};
</script>
<style lang="scss" scoped>
.input-wrapper {
  position: relative;
  cursor: pointer;
  width: 100%;
  &.is-focused {
    label,
    :slotted(label) {
      color: var(--color-accent);
    }
  }

  .input-container {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    .dropdown {
      width: 100%;

      .dropdown-toggle {
        width: 100%;
        min-width: 0;
        display: flex;
        align-items: center;
        border-radius: 7px;
        background: var(--color-bg-primary);
        border: 1px solid var(--color-border-primary);
        padding: 8px 30px 8px 12px;
        text-align: left;
        position: relative;

        color: var(--color-text-secondary);

        .select-trigger-text {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        font-family: Montserrat;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;

        &::after {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
        }

        &:focus {
          outline: 1.5px solid var(--color-accent);
          box-shadow: 0px 2px 6px 0px var(--color-shadow-medium);
        }
      }

      .dropdown-menu {
        width: 100%;
        padding: 0;
        max-height: 200px;
        overflow-y: auto;
        scrollbar-width: thin;
        scrollbar-color: var(--color-accent) var(--color-skeleton);
        background: var(--color-bg-secondary);

        .search-container {
          position: sticky;
          top: 0;
          z-index: 1;
          background: var(--color-bg-primary);
          padding: 8px;
          border-bottom: 1px solid var(--color-border-primary);

          .search-input {
            // Keep the existing dropdown styling for a browser search input.
            appearance: none;
            &::-webkit-search-cancel-button,
            &::-webkit-search-decoration {
              -webkit-appearance: none;
            }
            width: 100%;
            padding: 8px;
            border: 1px solid var(--color-border-primary);
            border-radius: 4px;
            font-family: Montserrat;
            font-size: 14px;
            color: var(--color-text-secondary);
            background: var(--color-bg-primary);

            &::placeholder {
              color: var(--color-text-muted);
            }

            &:focus {
              outline: 1.5px solid var(--color-accent);
              border-color: var(--color-accent);
            }
          }
        }

        &::-webkit-scrollbar {
          width: 6px;
        }

        &::-webkit-scrollbar-track {
          background: var(--color-skeleton);
          border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb {
          background: var(--color-accent);
          border-radius: 3px;
        }

        color: var(--color-text-primary);
        font-family: Montserrat;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;

        .dropdown-item {
          cursor: pointer;
          color: var(--color-text-secondary);
          display: flex;
          align-items: center;
          gap: 7px;

          .dropdown-item-icon {
            flex-shrink: 0;
          }
          &.active {
            background-color: var(--color-accent);
            color: var(--color-text-inverse);
          }
          &.keyboard-highlighted:not(.active) {
            background-color: var(--color-bg-hover);
            color: var(--color-text-primary);
          }
          &:hover {
            background-color: var(--color-bg-hover);
            color: var(--color-text-primary);
          }
        }
      }
    }

    :slotted(*) {
      position: absolute;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
    }
  }

  label,
  :slotted(label) {
    color: var(--color-text-muted);
    font-family: Montserrat;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: 0.3px;
    text-transform: uppercase;
  }
}

.validation-select {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  height: 0;
  width: 0;
  border: none;
  padding: 0;
  margin: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

.is-invalid {
  .dropdown-toggle {
    border-color: var(--color-error);
    &:focus {
      border-color: var(--color-error);
      box-shadow: 0 0 0 0.25rem var(--color-shadow-error);
    }
  }

  label,
  :slotted(label) {
    color: var(--color-error);
  }
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875em;
  color: var(--color-error);
  order: 3;
}
</style>
